import React, { useEffect, useState } from 'react';
import Layout from 'layout/Layout';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import {
  useGetAllMembersQuery,
  useUpdateMemberMutation,
  useDeleteMemberMutation,
} from 'api/consultancyApi';
import { mediaUrl } from 'utils/mediaUrl';

const isValidYouTubeUrl = url => {
  if (!url) return true;
  const regex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[\w-]{11}$/;
  return regex.test(url);
};

const Consultancy = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetAllMembersQuery();
  const [updateMember, { isLoading: isUpdating }] = useUpdateMemberMutation();
  const [deleteMember, { isLoading: isDeleting }] = useDeleteMemberMutation();
  const [members, setMembers] = useState([]);

  const [editingMemberId, setEditingMemberId] = useState(null);

  useEffect(() => {
    if (data?.data) {
      setMembers(
        data.data.map(member => ({
          ...member,
          profile_photo: null,
          existingPhoto: member.profile_photo,
          specialization_areas: Array.isArray(member.specialization_areas) ? member.specialization_areas.join(', ') : member.specialization_areas,
        }))
      );
    }
  }, [data]);

  const handleChange = (id, field, value) => {
    setMembers(prev =>
      prev.map(member => (member.id === id ? { ...member, [field]: value } : member))
    );
  };

  const handleFileChange = (id, file) => {
    setMembers(prev =>
      prev.map(member => (member.id === id ? { ...member, profile_photo: file } : member))
    );
  };

  const getImageUrl = (path) => {
    if (!path) return 'https://via.placeholder.com/150';
    return mediaUrl(path) || 'https://via.placeholder.com/150';
  };

  const handleDelete = async (slug) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This action will permanently delete the member.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        await deleteMember(slug).unwrap();
        Swal.fire({
          title: 'Deleted!',
          text: 'Member deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error('Delete error:', err);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete member.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d33',
        });
      }
    }
  };

  const handleUpdateSubmit = async (e, member) => {
    e.preventDefault();

    if (member.youtube_video_url && !isValidYouTubeUrl(member.youtube_video_url)) {
      return Swal.fire({
        icon: 'warning',
        title: 'Invalid URL',
        text: 'Please enter a valid YouTube video URL.',
      });
    }

    const formData = new FormData();
    if (member.profile_photo instanceof File) {
      formData.append('profile_photo', member.profile_photo);
    }

    const specializationList = member.specialization_areas
      ? member.specialization_areas.split(',').map(s => s.trim()).filter(Boolean)
      : [];
    formData.append('specialization_areas', JSON.stringify(specializationList));

    ['name', 'short_description', 'tagline', 'biography', 'youtube_channel_title', 'youtube_video_url'].forEach(field => {
      if (member[field] !== undefined) {
        formData.append(field, member[field]);
      }
    });

    try {
      await updateMember({ slug: member.slug, formData }).unwrap();
      Swal.fire({
        icon: 'success',
        title: 'Updated successfully!',
        showConfirmButton: false,
        timer: 1500
      });
      setEditingMemberId(null);
      refetch();
    } catch (error) {
      console.error('Update failed:', error);
      if (error?.data?.errors) {
        Swal.fire({
          icon: 'error',
          title: 'Update failed',
          text: Object.values(error.data.errors).flat().join(', '),
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Update failed',
          text: error?.data?.message || JSON.stringify(error),
        });
      }
    }
  };

  if (isLoading) return <p className="text-center mt-10">Loading...</p>;
  if (isError) return <p className="text-center mt-10 text-red-500">Error loading members.</p>;

  return (
    <Layout title="Manage Consultancy Team" tagline="Manage your Team Members">
      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14 md:mt-16">

        <div className="w-full flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-8">
          <h2 className="text-xl sm:text-2xl font-bold">Manage Members</h2>
          <div className="w-full sm:w-auto">
            <button
              onClick={() => navigate(`/create member`)}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm font-medium"
            >
              Create Member
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {members.map((member) => {
            if (editingMemberId !== null && editingMemberId !== member.id) {
              return null; // Hide other members while one is being edited
            }

            if (editingMemberId === member.id) {
              return (
                <form
                  key={member.id}
                  onSubmit={e => handleUpdateSubmit(e, member)}
                  className="bg-white rounded-lg border border-gray-300 shadow-md p-6"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-800">Edit Member: {member.name}</h3>
                    <button
                      type="button"
                      onClick={() => setEditingMemberId(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      ✕ Cancel
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                    {[
                      'name',
                      'short_description',
                      'tagline',
                      'specialization_areas',
                      'youtube_channel_title',
                      'youtube_video_url',
                    ].map(field => (
                      <div key={field} className="flex flex-col">
                        <label
                          htmlFor={`${member.id}-${field}`}
                          className="mb-2 text-gray-700 font-medium text-sm select-none"
                        >
                          {field.replace(/_/g, ' ')}
                        </label>
                        <input
                          id={`${member.id}-${field}`}
                          type="text"
                          value={member[field] || ''}
                          onChange={e => handleChange(member.id, field, e.target.value)}
                          className="border border-gray-300 rounded-md px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mb-6">
                    <label
                      htmlFor={`${member.id}-biography`}
                      className="block mb-2 text-gray-700 font-medium text-sm select-none"
                    >
                      Biography
                    </label>
                    <textarea
                      id={`${member.id}-biography`}
                      value={member.biography || ''}
                      onChange={e => handleChange(member.id, 'biography', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500"
                      rows={5}
                    />
                  </div>

                  <div className="flex items-center gap-6 mb-6">
                    {(member.profile_photo || member.existingPhoto) && (
                      <div className="w-24 h-24 rounded-full overflow-hidden border">
                        <img
                          src={
                            member.profile_photo
                              ? URL.createObjectURL(member.profile_photo)
                              : getImageUrl(member.existingPhoto)
                          }
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block mb-2 text-gray-700 font-medium text-sm">Change Photo</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => handleFileChange(member.id, e.target.files[0])}
                        className="text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setEditingMemberId(null)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isUpdating}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md text-sm font-medium"
                    >
                      {isUpdating ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              );
            }

            // View Mode (Card)
            return (
              <div
                key={member.id}
                className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md p-6 flex flex-col sm:flex-row gap-6 items-start sm:items-center transition"
              >
                <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 border bg-gray-100">
                  <img
                    src={getImageUrl(member.existingPhoto)}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { 
                      e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22150%22%20height%3D%22150%22%20viewBox%3D%220%200%20150%20150%22%3E%3Crect%20fill%3D%22%23ddd%22%20width%3D%22150%22%20height%3D%22150%22%2F%3E%3Ctext%20fill%3D%22%23555%22%20font-family%3D%22sans-serif%22%20font-size%3D%2216%22%20dy%3D%2210.5%22%20font-weight%3D%22bold%22%20x%3D%2250%25%22%20y%3D%2250%25%22%20text-anchor%3D%22middle%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E';
                    }}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800">{member.name}</h3>
                  <p className="text-gray-600 text-sm mb-1">{member.tagline}</p>
                  <p className="text-gray-500 text-xs mb-3 truncate max-w-xl">{member.specialization_areas}</p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => setEditingMemberId(member.id)}
                    className="flex-1 sm:flex-none bg-blue-100 text-blue-700 hover:bg-blue-200 px-4 py-2 rounded-md text-sm font-medium transition"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(member.slug)}
                    disabled={isDeleting}
                    className="flex-1 sm:flex-none bg-red-100 text-red-700 hover:bg-red-200 px-4 py-2 rounded-md text-sm font-medium transition"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => navigate(`/consultancyDetails/${member.slug}`)}
                    className="flex-1 sm:flex-none bg-green-100 text-green-700 hover:bg-green-200 px-4 py-2 rounded-md text-sm font-medium transition"
                  >
                    View
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </Layout>
  );
};

export default Consultancy;
